import { useState } from "react";
import type { FormEvent } from "react";
import { testimonials } from "@/mocks/homeContent";
import { SectionHead } from "./Common";

const FORM_URL = "https://readdy.ai/api/form/dagnpfptto0ceqqni180";

const VALUES = ["内容纠错", "线索投稿", "转载授权", "意见建议", "其他合作"];

const principles = [
  { icon: "ri-focus-3-line", title: "实事求是", desc: "能查证的信息尽量附上时间与出处，无法确认的明确标注求证中。" },
  { icon: "ri-list-check-3", title: "多方呈现", desc: "不只听单方说法，把不同声音放到同一张桌上，让读者自行判断。" },
  { icon: "ri-time-line", title: "时间线优先", desc: "先理清来龙去脉，再谈价值与立场，避免情绪先行的误导。" },
  { icon: "ri-heart-pulse-line", title: "克制表达", desc: "不用耸动标题制造恐慌，不为流量放大伤害，尊重边界与隐私。" },
];

export default function AboutContact() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 反垃圾：蜜罐命中则给出同样的成功反馈但不发送
    const hp = (fd.get("website_alt") as string) || "";
    if (hp.trim().length > 0) {
      setStatus("success");
      setFormError("");
      return;
    }
    fd.delete("website_alt");

    try {
      const res = await fetch(FORM_URL, { method: "POST", body: fd });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      if (res.ok && code === "OK") {
        setStatus("success");
        setFormError("");
        form.reset();
        return;
      }
      // 服务端校验/反垃圾失败一律走错误分支
      setStatus("error");
      setFormError(serverMsg.includes("spam") ? "提交被拦截，请稍后再试。" : serverMsg || "提交失败，请检查后重试。");
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  }

  return (
    <>
      {/* About */}
      <section id="about" className="bg-background-100 py-14 md:py-20 scroll-mt-16 md:scroll-mt-20">
        <div className="container-page grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <SectionHead eyebrow="About · 关于本站" title="把热闹看清楚，是我们想做的事" />
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-[15px]">
              每日黑料是一个专注娱乐与舆论热点的内容聚合门户。面对刷屏的消息、反转的说法与一浪高过一浪的情绪，
              我们选择退后一步，用时间线和多方核对的方式，把来龙去脉整理清楚再呈现给你。
            </p>
            <p className="mt-3 text-sm leading-loose text-foreground-600 md:text-[15px]">
              我们不追求最快发声，更在意每次更新是否经得起回头重看；不替读者站队，而是把判断所需的材料备齐。
              少一点以讹传讹，多一点可查证的耐心，是我们做内容的出发点。
            </p>

            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {principles.map((p) => (
                <div key={p.title} className="flex items-start gap-3 rounded-lg bg-background-50 p-4 ring-1 ring-background-300/50">
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className={p.icon}></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-sm font-bold text-foreground-950">{p.title}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-600">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 口碑 */}
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">读者怎么说</h3>
            <div className="mt-4 space-y-4">
              {testimonials.map((t) => (
                <figure key={t.name} className="rounded-lg bg-background-50 p-5 ring-1 ring-background-300/50">
                  <div className="flex gap-1 text-accent-500">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">“{t.text}”</blockquote>
                  <figcaption className="mt-4 flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 font-heading text-sm font-bold text-primary-700">
                      {t.name.slice(0, 1)}
                    </span>
                    <div className="leading-tight">
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="bg-background-50 py-14 md:py-20 scroll-mt-16 md:scroll-mt-20">
        <div className="container-page grid grid-cols-1 gap-8 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <SectionHead eyebrow="Contact · 联系订阅" title="留下你的反馈与线索" />
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
              无论是纠错、投稿、转载授权还是合作建议，都可以通过表单告诉我们。工作日通常会在 24 小时内回复。
            </p>
            <div className="mt-6 space-y-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-mail-send-line text-lg"></i>
                </span>
                <div className="leading-tight">
                  <p className="text-xs text-foreground-500">投稿 / 反馈邮箱</p>
                  <p className="text-sm font-medium text-foreground-900">contact@meiribilei.example</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-time-line text-lg"></i>
                </span>
                <div className="leading-tight">
                  <p className="text-xs text-foreground-500">回复时效</p>
                  <p className="text-sm font-medium text-foreground-900">工作日 24 小时内回复</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                  <i className="ri-shield-check-line text-lg"></i>
                </span>
                <div className="leading-tight">
                  <p className="text-xs text-foreground-500">隐私承诺</p>
                  <p className="text-sm font-medium text-foreground-900">反馈内容仅用于内部跟进，不会公开</p>
                </div>
              </div>
            </div>
          </div>

          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="lg:col-span-3 space-y-4 rounded-lg bg-background-100 p-5 ring-1 ring-background-300/50 md:p-7"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground-800">称呼</span>
                <input
                  name="name"
                  required
                  placeholder="怎么称呼你"
                  className="w-full rounded-md bg-background-50 px-4 py-3 text-sm text-foreground-900 ring-1 ring-background-300/70 outline-none transition focus:ring-2 focus:ring-primary-400 placeholder:text-foreground-400"
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground-800">邮箱</span>
                <input
                  name="email"
                  type="email"
                  required
                  placeholder="用于接收回复"
                  className="w-full rounded-md bg-background-50 px-4 py-3 text-sm text-foreground-900 ring-1 ring-background-300/70 outline-none transition focus:ring-2 focus:ring-primary-400 placeholder:text-foreground-400"
                />
              </label>
            </div>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-foreground-800">反馈类型</span>
              <div className="relative">
                <select
                  name="topic"
                  className="w-full appearance-none rounded-md bg-background-50 px-4 py-3 text-sm text-foreground-900 ring-1 ring-background-300/70 outline-none transition focus:ring-2 focus:ring-primary-400"
                >
                  {VALUES.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-lg text-foreground-500"></i>
              </div>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-foreground-800">留言内容</span>
              <textarea
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请尽量描述清楚，投稿请附上时间与来源等可核验信息（500 字以内）"
                className="w-full resize-none rounded-md bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 ring-1 ring-background-300/70 outline-none transition focus:ring-2 focus:ring-primary-400 placeholder:text-foreground-400"
              ></textarea>
              <span className="mt-1 block text-right text-xs text-foreground-400">最多 500 字</span>
            </label>

            {/* 反垃圾蜜罐 */}
            <label className="anti-bot" aria-hidden="true">
              网站地址
              <input name="website_alt" type="text" tabIndex={-1} autoComplete="off" readOnly />
            </label>

            {status === "error" && formError && (
              <p className="flex items-center gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
                <i className="ri-error-warning-line"></i>
                {formError}
              </p>
            )}
            {status === "success" && (
              <p className="flex items-center gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-800">
                <i className="ri-checkbox-circle-line text-lg"></i>
                收到！感谢你的反馈，我们会尽快处理并回复。
              </p>
            )}

            <button
              type="submit"
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
            >
              提交反馈
              <i className="ri-send-plane-line"></i>
            </button>
          </form>
        </div>
      </section>
    </>
  );
}